import { Room } from 'matrix-js-sdk';
import { IMatrixMessage } from '../message/types';
import { RoomEvent, RoomStateEvent, EventType } from "matrix-js-sdk";

// 使用 message/types 中定义的 IMatrixMessage 类型
export type { IMatrixMessage };

/**
 * 房间成员关系枚举
 * 用于表示用户与房间之间的关系状态
 * @enum {string}
 */
export enum RoomMembership {
  JOIN = 'join',      // 已加入房间
  INVITE = 'invite',  // 已被邀请
  LEAVE = 'leave',    // 已离开房间
  BAN = 'ban'        // 被封禁
}

/**
 * Matrix 房间基本信息接口
 * 用于展示房间列表和房间基本信息
 */
export interface IMatrixRoomInfo {
  roomId: string;           // 房间唯一标识
  name: string;            // 房间名称
  topic?: string;          // 房间主题/简介
  avatarUrl?: string;      // 房间头像URL
  unreadCount?: number;    // 未读消息数
  highlightCount?: number; // 提及/高亮消息数
}

/**
 * 布尔值状态接口
 * 用于表示各种加载、进行中的状态
 */
export interface IBooleanState {
  value: boolean;
}

/**
 * 房间消息ID映射
 * key: roomId - 房间ID
 * value: string[] - 该房间的消息ID数组
 */
export type RoomMessagesMap = Record<string, string[]>;

/**
 * 消息详情映射
 * key: messageId - 消息ID
 * value: IMatrixMessage - 消息详细内容
 */
export type MessageDetailsMap = Record<string, IMatrixMessage>;

/**
 * 房间成员列表映射
 * key: roomId - 房间ID
 * value: IRoomMember[] - 该房间的成员列表
 */
export type RoomMembersMap = Record<string, IRoomMember[]>;

/**
 * Matrix 房间数据接口
 * 用于存储和管理所有房间相关的数据
 */
export interface IMatrixRoomData {
  joinedRooms: IMatrixRoomInfo[];
  // 存储每个房间的消息 ID 数组
  messages: RoomMessagesMap;
  // 存储消息详情，key 为消息 ID
  messagesMap: MessageDetailsMap;
  // 存储房间成员信息
  roomMembers: RoomMembersMap;
  isFetchingMembers: IBooleanState;
}

/**
 * 事件处理器映射接口
 * 用于管理事件监听器
 */
export interface IEventHandlers {
  [event: string]: ((...args: any[]) => void)[];
}

/**
 * 房间管理器接口
 * 提供房间相关的核心功能
 */
export interface IRoomManager {
  init(): void;
  getRoomInfo(roomId: string): IMatrixRoomInfo | undefined;
  getRoomById(roomId: string): Promise<Room | null>;
  roomData: IMatrixRoomData;
  loadRoomMessages(roomId: string): Promise<void>;
  sendTextMessage(roomId: string, text: string): Promise<string>;
}

/**
 * 房间成员信息接口
 * 用于存储和展示成员信息
 */
export interface IRoomMember{
  userId: string;
  displayName?: string;
  avatarUrl?: string;
  membership: RoomMembership;
}

/**
 * 房间权限设置接口
 * 用于管理房间各项操作的权限级别
 */
export interface IRoomPermissions {
    sendMessages: number;
    invite: number;
    kick: number;
    ban: number;
    roomSettings: number;
}

/**
 * 房间隐私设置接口
 * 用于管理房间的隐私相关设置
 */
export interface IRoomPrivacy {
    isPublic: boolean;
    isEncrypted: boolean;
    allowGuestJoin: boolean;
}

/**
 * 房间设置接口
 * 包含房间的所有可配置项
 */
export interface IRoomSettings {
    name?: string;
    topic: string;
    avatarUrl: string | null;
    permissions: IRoomPermissions;
    privacy: IRoomPrivacy;
}

/**
 * 房间设置映射
 * key: roomId - 房间ID
 * value: IRoomSettings | null - 房间设置（null 表示房间不存在、获取失败或已删除）
 */
export type RoomSettingsMap = Record<string, IRoomSettings | null>;

/**
 * 房间加载状态映射
 * key: roomId - 房间ID
 * value: boolean - 是否正在加载
 */
export type RoomLoadingMap = Record<string, boolean>;

/**
 * 房间设置状态接口
 * 用于管理房间设置的状态
 */
export interface IRoomSettingsState {
    settings: RoomSettingsMap;
    isLoading: RoomLoadingMap;
}

/**
 * Matrix 房间相关事件一级类型（直接引用 matrix-js-sdk 的事件类型）
 */
export type MatrixRoomEventType = RoomEvent | RoomStateEvent;

/**
 * 房间业务事件二级类型
 * 例如：changed、removed、created、updated 等
 */
export enum RoomEventSubType {
    Changed = "changed",
    Removed = "removed",
    Created = "created",
    Updated = "updated"
}

/**
 * 组合事件类型：如 "m.room.name@changed"
 */
export type RoomBusinessEventType = `${MatrixRoomEventType}@${RoomEventSubType}`;

/**
 * 所有支持的房间业务事件常量（可扩展）
 */
export const RoomBusinessEventTypes = {
    NameChanged: `${EventType.RoomName}@${RoomEventSubType.Changed}` as const,
    TopicChanged: `${EventType.RoomTopic}@${RoomEventSubType.Changed}` as const,
    PowerLevelsChanged: `${EventType.RoomPowerLevels}@${RoomEventSubType.Changed}` as const,
    EncryptionChanged: `${EventType.RoomEncryption}@${RoomEventSubType.Changed}` as const,
    GuestAccessChanged: `${EventType.RoomGuestAccess}@${RoomEventSubType.Changed}` as const,
    // ...可继续扩展
};

/**
 * 事件参数类型定义
 */
export interface IRoomNameChangedPayload {
    roomId: string;
    name: string;
}
export interface IRoomTopicChangedPayload {
    roomId: string;
    topic: string;
}
export interface IRoomPowerLevelsChangedPayload {
    roomId: string;
    permissions: IRoomPermissions;
}
export interface IRoomEncryptionChangedPayload {
    roomId: string;
    isEncrypted: boolean;
}
export interface IRoomGuestAccessChangedPayload {
    roomId: string;
    allowGuestJoin: boolean;
}

/**
 * 房间业务事件类型映射（用于类型安全的 dispatcher）
 */
export interface IRoomBusinessEventMap {
    [key: string]: (payload: any) => void;
    [RoomBusinessEventTypes.NameChanged]: (payload: IRoomNameChangedPayload) => void;
    [RoomBusinessEventTypes.TopicChanged]: (payload: IRoomTopicChangedPayload) => void;
    [RoomBusinessEventTypes.PowerLevelsChanged]: (payload: IRoomPowerLevelsChangedPayload) => void;
    [RoomBusinessEventTypes.EncryptionChanged]: (payload: IRoomEncryptionChangedPayload) => void;
    [RoomBusinessEventTypes.GuestAccessChanged]: (payload: IRoomGuestAccessChangedPayload) => void;
    // ...可继续扩展
}